import { Button, Card, CardContent, FormHelperText, Grid, InputAdornment } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useLocation } from "react-router-dom";
import Breadcrumb from "../../Components/Navigation/Breadcrumbs/Breadcrumbs";
import Signifier from "../../Components/Signifier";
import { StyledTextField } from "../../Components/TextField/TextField";
import { selectUserInformation } from "../../State/Slices/UserInformationSlice";
import { ClientBillingTier, UpdateClientBillingTier } from "../../Types/ClientBilling";
import { updateClientBillingTier } from "../../Services/API/ClientBillingsTier";
import { BillingTier } from "../../Enums/BillingTier";
import {
  updateBillingTiersData,
  useClientBillingTiers,
} from "../../Helpers/billingTierDefaultPricing";
import { validateCreateBillingTierData } from "../../Components/Modal/CreateBillingTierModal";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "90%",
    marginBottom: "50px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  cardContainer: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none",
      width: "100%",
      height: "85%",
      overflowY: "scroll",
    },
    width: "85%",
    height: "90%",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
  },
  error: {
    color: theme.palette.error.main,
  },
  header: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      marginTop: "10px",
    },
    display: "flex",
  },
  descLabel: {
    margin: "0px",
    fontWeight: 700,
    marginBottom: "10px",
  },
  descValue: {
    margin: "0px",
    marginLeft: "5px",
    marginRight: "20px",
  },
  descBlock: {
    display: "flex",
  },
  sensitiveButton: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none",
      width: "100%",
    },
    width: "200px",
  },
}));

function BillingTiersDetailPage() {
  const classes = useStyles();
  const dispatch = useDispatch();

  let location = useLocation<ClientBillingTier>();
  const { "http://nasrecruitment.io/activateId": userId } = useSelector(selectUserInformation);
  const { tiers } = useClientBillingTiers();
  const [tierName, setTierName] = useState(location.state?.tierName ?? "");
  const [monthlyBasePrice, setMonthlyBasePrice] = useState(location.state.monthlyBasePrice ?? 0);
  const [candidateQuota, setCandidateQuota] = useState(location.state.candidateAllowance ?? 0);
  const [candidateRate, setCandidateRate] = useState(location.state.candidateRate ?? 0);
  const [emailQuota, setEmailQuota] = useState(location.state.emailAllowance ?? 0);
  const [emailRate, setEmailRate] = useState(location.state.emailRate ?? 0);
  const [textQuota, setTextQuota] = useState(location.state.textAllowance ?? 0);
  const [textRate, setTextRate] = useState(location.state.textRate ?? 0);
  const [showUpdateProcessing, setShowUpdateProcessing] = useState(false);
  const [showSignifier, setShowSignifier] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const [showInputError, setShowInputError] = useState(false);

  const submitData = useCallback(async () => {
    const updateTierData = {
      userId,
      tierId: location.state.id,
      tierName,
      monthlyBasePrice,
      candidateQuota,
      candidateRate,
      emailQuota,
      emailRate,
      textQuota,
      textRate,
    } as UpdateClientBillingTier;

    if (validateCreateBillingTierData(updateTierData, tiers, true)) {
      setShowUpdateProcessing(true);
      try {
        await updateClientBillingTier(updateTierData);
        updateBillingTiersData(dispatch);
      } catch (error) {
        setShowSubmitError(true);
      } finally {
        setShowSignifier(true);
        setShowUpdateProcessing(false);
      }
    } else {
      setShowInputError(true);
    }
  }, [
    candidateQuota,
    candidateRate,
    dispatch,
    emailQuota,
    emailRate,
    location.state.id,
    monthlyBasePrice,
    textQuota,
    textRate,
    tierName,
    tiers,
    userId,
  ]);

  if (location.state === undefined) {
    return <Redirect to="/billingtiers" />;
  }

  return (
    <>
      <Signifier
        showSignifier={showSignifier}
        setShowSignifier={setShowSignifier}
        message="Tier Saved"
        error={showSubmitError}
        errorMessage={"Error Saving Tier, Please Try Again"}
      />
      <div className="page-container">
        <Breadcrumb />
        <h1 className="page-title">Billing Tier Detailed</h1>
        <div className={classes.header}>
          <div className={classes.descBlock}>
            <p className={classes.descLabel}>CREATED BY: </p>
            <p className={classes.descValue}>{location.state.createdBy}</p>
          </div>
          <div className={classes.descBlock}>
            <p className={classes.descLabel}>CREATED ON: </p>
            <p className={classes.descValue}>
              {new Date(location.state.createdDate).toLocaleString()}
            </p>
          </div>
        </div>
        <Grid className={classes.cardContainer}>
          <Card>
            <CardContent>
              <Grid container spacing={1}>
                <Grid xs={12} sm={12} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    placeholder="Name"
                    label="Name"
                    variant="standard"
                    fullWidth
                    required
                    value={tierName}
                    style={{ fontWeight: 700 }}
                    onChange={(e) => setTierName(e.target.value as BillingTier)}
                  />
                  {showInputError && !!tierName && (
                    <FormHelperText className={classes.error}>Tier Name Required</FormHelperText>
                  )}
                </Grid>
                <Grid xs={12} sm={12} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    type="number"
                    placeholder="Base Price"
                    label="Base Price"
                    variant="standard"
                    fullWidth
                    required
                    value={monthlyBasePrice}
                    onChange={(e) => setMonthlyBasePrice(Number(e.target.value))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  {showInputError && monthlyBasePrice < 0 && (
                    <FormHelperText className={classes.error}>Base Price Required</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    type="number"
                    placeholder="Candidate Allowance"
                    label="Candidate Allowance"
                    variant="standard"
                    fullWidth
                    required
                    value={candidateQuota}
                    onChange={(e) => setCandidateQuota(Number(e.target.value))}
                  />
                  {showInputError && candidateQuota < 0 && (
                    <FormHelperText className={classes.error}>
                      Candidate Allowance Required
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    type="number"
                    placeholder="Price Per Overage Candidate"
                    label="Price Per Overage Candidate"
                    variant="standard"
                    fullWidth
                    required
                    value={candidateRate}
                    onChange={(e) => setCandidateRate(Number(e.target.value))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  {showInputError && candidateRate < 0 && (
                    <FormHelperText className={classes.error}>
                      Price Per Overage Candidate Required
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    type="number"
                    placeholder="Email Allowance"
                    label="Email Allowance"
                    value={emailQuota}
                    onChange={(e) => setEmailQuota(Number(e.target.value))}
                    variant="standard"
                    fullWidth
                    required
                  />
                  {showInputError && emailQuota < 0 && (
                    <FormHelperText className={classes.error}>
                      Email Allowance Required
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    type="number"
                    placeholder="Price Per Overage Email"
                    label="Price Per Overage Email"
                    value={emailRate}
                    onChange={(e) => setEmailRate(Number(e.target.value))}
                    variant="standard"
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  {showInputError && emailRate < 0 && (
                    <FormHelperText className={classes.error}>
                      Price Per Overage Email Required
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    type="number"
                    placeholder="Text Message Allowance"
                    label="Text Message Allowance"
                    value={textQuota}
                    onChange={(e) => setTextQuota(Number(e.target.value))}
                    variant="standard"
                    fullWidth
                    required
                  />
                  {showInputError && textQuota < 0 && (
                    <FormHelperText className={classes.error}>
                      Text Message Allowance Required
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    type="number"
                    placeholder="Price Per Overage Text Message"
                    label="Price Per Overage Text Message"
                    value={textRate}
                    onChange={(e) => setTextRate(Number(e.target.value))}
                    variant="standard"
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  {showInputError && textRate < 0 && (
                    <FormHelperText className={classes.error}>
                      Price Per Overage Text Message Required
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={12} container justifyContent="flex-end">
                  <Button
                    style={{ marginTop: "20px", marginRight: "8px" }}
                    className={classes.sensitiveButton}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={submitData}
                    disabled={showUpdateProcessing}
                  >
                    Save Information
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </div>
    </>
  );
}

export default BillingTiersDetailPage;
